import { useState, useEffect } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { FileText, BookMarked, Plus, X, Search, Loader2, Building2, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = ['contract', 'nda', 'policy', 'email', 'sop', 'memo', 'proposal', 'job_description', 'general'];

const DEST_OPTIONS = [
  { value: 'auto',      label: 'Auto (route to contact folder if attached, else Organization Files)' },
  { value: 'firm',      label: 'Organization Files (shared with team)' },
  { value: 'personal',  label: 'My Files (private to me)' },
];

export default function SaveOutputDialog({ open, onOpenChange, content, hub, sourceThreadId, defaultMode = 'document' }) {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const [mode, setMode] = useState(defaultMode);
  const [destination, setDestination] = useState('auto');
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('general');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [contacts, setContacts] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [tplDescription, setTplDescription] = useState('');
  const [tplCategory, setTplCategory] = useState('general');
  const [tplContent, setTplContent] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset when opened with new content
  useEffect(() => {
    if (open) {
      setMode(defaultMode);
      setTitle('');
      setDestination('auto');
      setDocType('general');
      setTags([]);
      setTagInput('');
      setSelectedContactIds([]);
      setContactSearch('');
      setTplDescription('');
      setTplCategory('general');
      setTplContent(content || '');
    }
  }, [open, content, defaultMode]);

  useEffect(() => {
    if (open && wsId) {
      apiFetch(`${API_URL}/api/contacts?workspace_id=${wsId}`)
        .then(r => r.ok ? r.json() : [])
        .then(setContacts)
        .catch(() => {});
    }
  }, [open, wsId]);

  const addTag = (tag) => {
    const t = tag.trim();
    if (!t || tags.includes(t)) return;
    setTags(prev => [...prev, t]);
    setTagInput('');
  };

  const filteredContacts = contacts.filter(c =>
    !contactSearch || c.name?.toLowerCase().includes(contactSearch.toLowerCase())
  );

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (mode === 'document') {
        const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/ai-documents`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: title.trim(),
            content,
            hub,
            doc_type: docType,
            source_thread_id: sourceThreadId,
            attached_contact_ids: selectedContactIds,
            tags,
            destination: destination === 'auto' ? null : destination,
          }),
        });
        if (res.ok) {
          toast.success('Saved to Documents Library');
          onOpenChange(false);
        } else { toast.error('Failed to save'); }
      } else {
        const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/ai-templates`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: title.trim(),
            description: tplDescription,
            content: tplContent,
            hub,
            category: tplCategory,
          }),
        });
        if (res.ok) {
          toast.success('Saved as Template');
          onOpenChange(false);
        } else { toast.error('Failed to save'); }
      }
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  // Detect placeholder count in template content for the user
  const placeholderMatches = [...(tplContent || '').matchAll(/\{\{([^}]+)\}\}/g)];
  const detectedPlaceholders = [...new Set(placeholderMatches.map(m => m[1].trim()))];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="save-output-dialog">
        <DialogHeader>
          <DialogTitle>Save AI Output</DialogTitle>
        </DialogHeader>

        <Tabs value={mode} onValueChange={setMode} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="document" data-testid="save-mode-document">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> As Document
            </TabsTrigger>
            <TabsTrigger value="template" data-testid="save-mode-template">
              <BookMarked className="w-3.5 h-3.5 mr-1.5" /> As Template
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto pr-2 mt-3">
            <TabsContent value="document" className="space-y-3 mt-0">
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Save to</Label>
                <Select value={destination} onValueChange={setDestination}>
                  <SelectTrigger data-testid="save-destination-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEST_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">
                  Tip: Attach a client or vendor below and the document will automatically land in their folder.
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Title *</Label>
                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., NDA with Acme Corp" data-testid="doc-title-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Document Type</Label>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Attach to Contact(s)</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input value={contactSearch} onChange={e => setContactSearch(e.target.value)} placeholder="Search clients & vendors..." className="pl-9 h-9" data-testid="contact-search-input" />
                </div>
                {selectedContactIds.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {selectedContactIds.map(cid => {
                      const c = contacts.find(x => x.contact_id === cid);
                      return (
                        <Badge key={cid} className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 pr-0.5">
                          {c?.name || cid}
                          <button onClick={() => setSelectedContactIds(prev => prev.filter(x => x !== cid))} className="ml-1 hover:bg-black/10 rounded-full p-0.5">
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
                {contactSearch && (
                  <div className="max-h-32 overflow-y-auto rounded-md border bg-card mt-1">
                    {filteredContacts.slice(0, 8).map(c => (
                      <button
                        key={c.contact_id}
                        type="button"
                        onClick={() => {
                          if (!selectedContactIds.includes(c.contact_id)) {
                            setSelectedContactIds(prev => [...prev, c.contact_id]);
                          }
                          setContactSearch('');
                        }}
                        className="w-full text-left px-3 py-1.5 hover:bg-muted/50 text-sm flex items-center justify-between"
                      >
                        <span>{c.name}</span>
                        <Badge variant="secondary" className="text-[9px] capitalize">{c.contact_type}</Badge>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Tags</Label>
                <div className="flex flex-wrap gap-1 mb-1.5">
                  {tags.map(t => (
                    <Badge key={t} variant="secondary" className="text-[10px] pr-0.5">
                      {t}
                      <button onClick={() => setTags(prev => prev.filter(x => x !== t))} className="ml-1 hover:bg-black/10 rounded-full p-0.5">
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput); } }}
                    placeholder="Add tag..."
                    className="h-8"
                    data-testid="tag-input"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={() => addTag(tagInput)}><Plus className="w-3 h-3" /></Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Preview</Label>
                <div className="text-xs text-muted-foreground bg-muted/40 rounded p-2 max-h-24 overflow-y-auto whitespace-pre-wrap">
                  {(content || '').slice(0, 400)}{(content || '').length > 400 ? '...' : ''}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="template" className="space-y-3 mt-0">
              <div className="space-y-1">
                <Label className="text-xs">Template Title *</Label>
                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Standard NDA Template" data-testid="tpl-title-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Description</Label>
                <Input value={tplDescription} onChange={e => setTplDescription(e.target.value)} placeholder="When and how to use this template..." />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Category</Label>
                <Select value={tplCategory} onValueChange={setTplCategory}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="policy">Policy</SelectItem>
                    <SelectItem value="proposal">Proposal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Template Content (use <code className="bg-muted px-1 rounded">{'{{placeholder}}'}</code> for variables)</Label>
                <Textarea
                  value={tplContent}
                  onChange={e => setTplContent(e.target.value)}
                  rows={8}
                  className="font-mono text-xs"
                  placeholder="e.g., Hi {{client_name}}, thank you for your interest..."
                  data-testid="tpl-content-textarea"
                />
                {detectedPlaceholders.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className="text-[10px] text-muted-foreground">Detected placeholders:</span>
                    {detectedPlaceholders.map(p => (
                      <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground">Tip: Add <code className="bg-muted px-1 rounded text-[10px]">{'{{client_name}}'}</code>, <code className="bg-muted px-1 rounded text-[10px]">{'{{date}}'}</code>, <code className="bg-muted px-1 rounded text-[10px]">{'{{amount}}'}</code> etc. to make this reusable.</p>
              </div>
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter className="border-t pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!title.trim() || saving} data-testid="save-output-confirm">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Save {mode === 'document' ? 'Document' : 'Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
